import { useCallback, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import classNames from "classnames";
import _ from "lodash";

import styles from "./AscActivitiesAddNewCard.module.scss";
import AscButton from "../AscButton/AscButton";
import AscFilesUploader from "../AscFilesUploader/AscFilesUploader";
import AscSelectInput from "../AscInputFields/AscSelectInput/AscSelectInput";
import AscTextInput from "../AscInputFields/AscTextInput/AscTextInput";
import AscResourcesBanner from "../AscResourcesBanner/AscResourcesBanner";

export interface AscActivitiesAddNewCardProps {
  className?: string;
  imageUrl?: string;
  cardType?: "skill" | "track";
  cardId?: string;
  onSubmit: (values: any) => void;
  skillsCategories?: any; //TODO: fix type
}

enum BadgeColorTypes {
  TEAL = "TEAL",
  PINK = "PINK",
  GREEN = "GREEN",
  PURPLE = "PURPLE",
  ORANGE = "ORANGE",
}
type FaceTypes = "woman-1" | "woman-2" | "man-1" | "man-2" | "man-3";
type VariantNumbers = 1 | 2 | 3 | 4 | 5 | 6 | 7;
const faceValues: FaceTypes[] = [
  "woman-1",
  "woman-2",
  "man-1",
  "man-2",
  "man-3",
];

const variantValues: VariantNumbers[] = [1, 2, 3, 4, 5, 6, 7];

export default function AscActivitiesAddNewCard({
  className,
  imageUrl,
  cardType,
  cardId,
  onSubmit,
  skillsCategories,
}: AscActivitiesAddNewCardProps) {
  const getCurrentSkill = () => {
    const { skills } =
      _.find(skillsCategories, {
        skills: [{ id: cardId }],
      }) || {};
    const currentSkill = _.find(skills, { id: cardId });
    return currentSkill;
  };

  const handleUpload = useCallback((acceptedFiles: any[]) => {
    formik.setFieldValue("image", acceptedFiles[0]);
  }, []);

  const handleRemove = useCallback(() => {
    formik.setFieldValue("image", null);
  }, []);

  const handleBadgeUpload = useCallback((acceptedFiles: File[]) => {
    formik.setFieldValue("badge", acceptedFiles[0]);
  }, []);

  const handleBadgeRemove = useCallback(() => {
    formik.setFieldValue("badge", null);
  }, []);

  const getInitialValues = () => {
    const prePopulatedInitialValues =
      cardType === "skill"
        ? getCurrentSkill()
        : _.find(skillsCategories, { id: cardId });

    return {
      name: prePopulatedInitialValues?.name || "",
      image: prePopulatedInitialValues?.challengeThumbnail?.location,
      badgeColor: prePopulatedInitialValues?.badgeColor || "",
      badge: prePopulatedInitialValues?.badge?.location || "",
      variant: prePopulatedInitialValues?.banner?.variant || "",
      face: prePopulatedInitialValues?.banner?.face || "",
      text: prePopulatedInitialValues?.banner?.text || "",
      secondText: prePopulatedInitialValues?.banner?.secondText || "",
    };
  };

  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema: Yup.object({
      name: Yup.string().required("Please enter name"),
    }),
    onSubmit: onSubmit,
  });
  const { badgeColor, variant, face, text, secondText } = formik.values;

  useEffect(() => {
    const initialValues = getInitialValues();
    formik.setFieldValue("name", getInitialValues().name);
    formik.setFieldValue("image", getInitialValues().image);
    formik.setFieldValue("badgeColor", initialValues.badgeColor);
    formik.setFieldValue("badge", getInitialValues().badge);
    formik.setFieldValue("variant", getInitialValues().variant);
    formik.setFieldValue("face", getInitialValues().face);
    formik.setFieldValue("text", getInitialValues().text);
    formik.setFieldValue("secondText", getInitialValues().secondText);
  }, [skillsCategories, cardId]);

  return (
    <div
      data-testid="asc-activities-add-new-card"
      className={classNames(styles.ground, className)}
    >
      <form className={styles.inputGround} onSubmit={formik.handleSubmit}>
        <div className={styles.inputField}>
          <label htmlFor="name">
            Skill name <span>*</span>
          </label>
          <AscTextInput
            className={classNames(styles.input, {
              [styles.errorInput]: formik.errors.name && formik.touched.name,
            })}
            fullWidth={true}
            id="name"
            name="name"
            placeholder="Skill name"
            onChange={formik.handleChange}
            // onBlur={formik.handleBlur}
            value={formik.values.name}
          />
          {formik.errors.name && formik.touched.name && (
            <div className={styles.fieldError}>
              {formik.errors.name.toString()}
            </div>
          )}
        </div>

        <div className={styles.inputField}>
          <label htmlFor="badgeColor">
            Badge Color<span>*</span>
          </label>
          <AscSelectInput
            id="badgeColor"
            name="badgeColor"
            className={styles.input}
            value={formik.values.badgeColor}
            placeholder="Select Badge color"
            onChange={(val) => formik.setFieldValue("badgeColor", val)}
            defaultValue={
              "badgeColor" in formik.values && formik.values.badgeColor
            }
          >
            {Object.keys(BadgeColorTypes).map((key) => (
              <AscSelectInput.Option key={key} value={key} label={key} />
            ))}
          </AscSelectInput>
        </div>

        {cardType === "skill" ? (
          <div className={classNames(styles.inputField, styles.thumbnailField)}>
            <label htmlFor="image" className={styles.uploadLabel}>
              Challenge Thumbnail <span>*</span>
            </label>
            <AscFilesUploader
              name="image"
              type="image"
              accept={{
                "image/*": [".jpeg", ".png"],
              }}
              multiple={false}
              onDrop={handleUpload}
              onRemoveFile={handleRemove}
              srcFileClassName={styles.srcFileClassName}
              srcFile={(() => {
                try {
                  if (formik?.values?.image instanceof File) {
                    return URL.createObjectURL(formik.values.image);
                  } else if (formik?.values?.image) {
                    return formik?.values?.image;
                  }
                } catch (e) {
                  console.error(e);
                }
                return undefined;
              })()}
              className={styles.image}
            />
          </div>
        ) : null}
        <div className={classNames(styles.inputField, styles.badgeField)}>
          <label htmlFor="image" className={styles.uploadLabel}>
            Badge <span>*</span>
          </label>
          <AscFilesUploader
            name="badge"
            compact
            type="image"
            accept={{
              "image/*": [".jpeg", ".png"],
            }}
            multiple={false}
            onDrop={handleBadgeUpload}
            onRemoveFile={handleBadgeRemove}
            srcFileClassName={styles.srcFileClassName}
            srcFile={(() => {
              try {
                if (formik?.values?.badge instanceof File) {
                  return URL.createObjectURL(formik.values.badge);
                } else if (formik?.values?.badge) {
                  return formik?.values?.badge;
                }
              } catch (e) {
                console.error(e);
              }
              return undefined;
            })()}
            className={styles.image}
          />
        </div>
        {cardType === "skill" ? (
          <div className={styles.bannerFieldsContainer}>
            <div className={styles.inputField}>
              <label htmlFor="variant">
                Variant <span>*</span>
              </label>
              <AscSelectInput
                id="variant"
                name="variant"
                className={styles.input}
                value={formik.values.variant}
                placeholder="Select Variant"
                onChange={(val) => formik.setFieldValue("variant", val)}
                defaultValue={
                  "variant" in formik.values && formik.values.variant
                }
              >
                {variantValues.map((key) => (
                  <AscSelectInput.Option
                    key={key}
                    value={key}
                    label={String(key)}
                  />
                ))}
              </AscSelectInput>
            </div>
            <div className={styles.inputField}>
              <label htmlFor="face">Face</label>
              <AscSelectInput
                id="face"
                name="face"
                className={styles.input}
                value={formik.values.face}
                placeholder="Select face"
                onChange={(val) => formik.setFieldValue("face", val)}
                defaultValue={"face" in formik.values && formik.values.face}
              >
                {faceValues.map((key) => (
                  <AscSelectInput.Option key={key} value={key} label={key} />
                ))}
              </AscSelectInput>
            </div>
            <div className={styles.inputField}>
              <label htmlFor="text">
                Banner Text <span>*</span>
              </label>
              <AscTextInput
                className={classNames(styles.input, {
                  [styles.errorInput]:
                    formik.errors.text && formik.touched.text,
                })}
                fullWidth={true}
                id="text"
                name="text"
                placeholder="Add Text"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.text}
              />
              {formik.errors.text && formik.touched.text && (
                <div className={styles.fieldError}>
                  {formik.errors.text.toString()}
                </div>
              )}
            </div>
            <div className={styles.inputField}>
              <label htmlFor="secondText">Banner Second Text</label>
              <AscTextInput
                className={classNames(styles.input, {
                  [styles.errorInput]:
                    formik.errors.secondText && formik.touched.secondText,
                })}
                fullWidth={true}
                id="secondText"
                name="secondText"
                placeholder="Add Second Text"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.secondText}
              />
              {formik.errors.secondText && formik.touched.secondText && (
                <div className={styles.fieldError}>
                  {formik.errors.secondText.toString()}
                </div>
              )}
            </div>
          </div>
          
        ) : null}
          {cardType === "skill" &&
            (badgeColor || variant || face || text || secondText) ? (
              <div className={styles.previewContainer}>
                <AscResourcesBanner
                  color={badgeColor}
                  variant={variant}
                  face={face}
                  text={text}
                  secondText={(variant === 1 || variant === 4) && secondText}
                />
              </div>
            ) : null}

        <div className={styles.footerSave}>
          <AscButton type="submit" disabled={!(formik.isValid && formik.dirty)}>
            {cardId ? "Save changes" : "Save"}
          </AscButton>
        </div>
      </form>
    </div>
  );
}
